import { Grid } from "../core/Grid.ts";
import { Pathfinding } from "../core/Pathfinding.ts";
import { PathRegistry } from "../core/PathRegistry.ts";
import { CellType } from "../core/types.ts";
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  SPAWN_SEED_SALT,
  BLOCKER_SEED_SALT,
  DEFAULT_SPAWN_POLICY,
  type SpawnPolicy,
} from "../core/config.ts";
import type { Vec2 } from "../core/types.ts";
import type { RuntimeState } from "../game/types.ts";
import { createRng, type Rng } from "../core/Rng.ts";
import {
  type SpawnCell,
  type SpawnLayout,
  cloneSpawnLayout,
} from "../core/SpawnModel.ts";

/* BL-145 / D-247: the placement policy is now data-driven — the former
 * `MIN_SPAWN_SEPARATION` (Bug 9, =4) and `MAX_BREACH_PRIMARY_OVERLAP` (BL-137,
 * =0.5) literals live on `SpawnPolicy` (`config.ts`), default-identical. The
 * BL-137 overlap rationale: a spawn whose breach-path shares more than the cap
 * with the primary doesn't FLANK — it joins the primary lane (the S141 "off-
 * wall" no-op). Left-wall (col 0) spawns overlapped 33–91 % since their A*
 * route down col 0 hits PATH_ENTRY and walks the primary line to EXIT; the
 * top/bottom fair zone keeps overlap ≤ 0.15, and the cap is the safety net for
 * blocker-deflected outliers. Pure data check; deterministic. */

export interface GridSetupResult {
  grid: Grid;
  pathfinding: Pathfinding;
  pathRegistry: PathRegistry;
  recalculatePath: () => void;
  primaryPathCellCount: number;
  resetSpawnLayout: (seed: number) => SpawnLayout;
  /** Re-roll the procedural blockers for a new run. Call BEFORE
   *  {@link GridSetupResult.resetSpawnLayout} for the same seed. */
  regenerateBlockers: (seed: number) => void;
  getSpawnLayout: () => SpawnLayout;
}

/** BL-137 / D-242: the FAIR-FLANK zone — TOP wall (row 0) and BOTTOM wall
 *  (row {@link GRID_HEIGHT} − 1), cols 1..10. The left wall (col 0) is
 *  dropped: PATH_ENTRY sits at (0, GRID_HEIGHT/2) so any spawn on col 0 has
 *  its breach route go DOWN col 0 to PATH_ENTRY and then walk the primary
 *  lane to EXIT — overlap 33–91 % across seeds, the "joined the existing
 *  lane" bug from the owner's screenshot. Top/bottom-wall spawns are 6–7
 *  rows off the primary, so their A* route takes a real flanking detour
 *  (overlap typically ≤ 0.15). Cols 1..10 keep the spawns on the
 *  player-side half (the breach has to cross the board), not gifted near
 *  the exit. */
function isEligibleSpawnCandidate(
  grid: Grid,
  col: number,
  row: number,
  policy: SpawnPolicy,
): boolean {
  const cell = grid.getCell(col, row);
  if (!cell || cell.type !== CellType.EMPTY) return false;
  if (col < policy.fairZoneColMin || col > policy.fairZoneColMax) return false;
  return row === 0 || row === grid.height - 1;
}

/** BL-137 / D-242: fraction of the candidate's BREACH path (spawn → EXIT)
 *  that overlaps the primary path (PATH_ENTRY → EXIT). 0 = totally flanking;
 *  near-1 = the breach is just the primary with a short prefix. Used to drop
 *  candidates that don't actually flank (the on-route bug). */
function breachOverlapFraction(
  pathfinding: Pathfinding,
  pathGrid: { width: number; height: number; isBlocked: (x: number, y: number) => boolean },
  spawn: SpawnCell,
  exit: Vec2,
  primarySet: Set<string>,
): number | null {
  const path = pathfinding.findPathUncached(
    pathGrid,
    { x: spawn.col, y: spawn.row },
    exit,
  );
  if (!path || path.length === 0) return null;
  let shared = 0;
  for (const p of path) {
    if (primarySet.has(`${p.x},${p.y}`)) shared++;
  }
  return shared / path.length;
}

function collectEligibleSpawnCandidates(
  grid: Grid,
  pathfinding: Pathfinding,
  entry: Vec2,
  exit: Vec2,
  policy: SpawnPolicy,
): SpawnCell[] {
  const pathGrid = {
    width: grid.width,
    height: grid.height,
    isBlocked: (gx: number, gy: number) =>
      grid.getCell(gx, gy)?.blocksPath ?? false,
  };
  // BL-137 / D-242: compute the primary path ONCE so the per-candidate
  // overlap check is cheap. Bail to no-overlap-filtering if primary itself
  // is unreachable (createGrid invariant violated upstream → caller throws).
  const primaryPath = pathfinding.findPathUncached(pathGrid, entry, exit);
  const primarySet = new Set<string>(
    primaryPath?.map((p) => `${p.x},${p.y}`) ?? [],
  );

  const candidates: SpawnCell[] = [];
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      if (!isEligibleSpawnCandidate(grid, col, row, policy)) continue;
      const path = pathfinding.findPathUncached(
        pathGrid,
        { x: col, y: row },
        exit,
      );
      if (!path || path.length < policy.minBreachPathLength) continue;
      // BL-137 / D-242: drop candidates whose breach route mostly walks the
      // primary lane — they're "off-wall" in name only. (No-op for the new
      // top/bottom fair zone in the clean case; a safety net when blockers
      // deflect a breach back onto primary.)
      const overlap = breachOverlapFraction(
        pathfinding,
        pathGrid,
        { col, row },
        exit,
        primarySet,
      );
      if (overlap !== null && overlap > policy.maxBreachPrimaryOverlap) {
        continue;
      }
      candidates.push({ col, row });
    }
  }

  return candidates;
}

function chooseSpawnLayout(
  grid: Grid,
  pathfinding: Pathfinding,
  seed: number,
  policy: SpawnPolicy,
): SpawnLayout {
  const entry = { x: 0, y: Math.floor(GRID_HEIGHT / 2) };
  const exit = { x: GRID_WIDTH - 1, y: Math.floor(GRID_HEIGHT / 2) };
  const rng = createRng((seed ^ SPAWN_SEED_SALT) | 0);
  const candidates = collectEligibleSpawnCandidates(
    grid,
    pathfinding,
    entry,
    exit,
    policy,
  );

  if (candidates.length < policy.count) {
    throw new Error("Insufficient eligible spawn positions for seeded layout");
  }

  // Bug 9: prefer candidates at least `policy.minSeparation` cells (Chebyshev)
  // from every already-chosen spawn so the spawns can't cluster directly
  // adjacent; relax to the full pool only if the fair zone can't fit `count`
  // (never throws). Deterministic — one rng.int per pick on an ISOLATED stream.
  const pool = [...candidates];
  const chosen: SpawnCell[] = [];
  while (chosen.length < policy.count && pool.length > 0) {
    const far = pool.filter((p) =>
      chosen.every(
        (c) =>
          Math.max(Math.abs(c.col - p.col), Math.abs(c.row - p.row)) >=
          policy.minSeparation,
      ),
    );
    const src = far.length > 0 ? far : pool;
    const pick = src[rng.int(src.length)]!;
    pool.splice(pool.indexOf(pick), 1);
    chosen.push(pick);
  }

  return {
    dormant: chosen.slice(0, policy.dormantCount),
    hidden: chosen.slice(policy.dormantCount, policy.count),
  };
}

function clearSpawnLayout(grid: Grid): void {
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      const cell = grid.getCell(col, row);
      if (
        cell?.type === CellType.DORMANT_SPAWN ||
        cell?.type === CellType.HIDDEN_SPAWN
      ) {
        grid.setCell(col, row, CellType.EMPTY);
      }
    }
  }
}

function applySpawnLayout(grid: Grid, layout: SpawnLayout): void {
  clearSpawnLayout(grid);
  for (const pos of layout.dormant) {
    grid.setCell(pos.col, pos.row, CellType.DORMANT_SPAWN);
  }
  for (const pos of layout.hidden) {
    grid.setCell(pos.col, pos.row, CellType.HIDDEN_SPAWN);
  }
}

export function createGrid(
  gs: RuntimeState,
  rng: Rng,
  policy: SpawnPolicy = DEFAULT_SPAWN_POLICY,
): GridSetupResult {
  const grid = new Grid(GRID_WIDTH, GRID_HEIGHT);
  const pathfinding = new Pathfinding();
  const pathRegistry = new PathRegistry(pathfinding);
  let spawnLayout: SpawnLayout = { dormant: [], hidden: [] };

  grid.setOnCellChanged(() => {
    pathfinding.invalidateCache();
    pathRegistry.recalculateAll();
    // Sync primary pixel path to gs for consumers that read gs.pathPixels
    gs.pathPixels = pathRegistry.getPixelPath("primary") ?? [];
  });

  // Setup grid entry/exit
  grid.setCell(0, Math.floor(GRID_HEIGHT / 2), CellType.PATH_ENTRY);
  grid.setCell(GRID_WIDTH - 1, Math.floor(GRID_HEIGHT / 2), CellType.PATH_EXIT);

  // CR-31: Procedural grid blockers (3–6, path-validated).
  // BL-117: bias placement into the central lane band (within LANE_BAND rows of
  // the entry/exit row) so blockers actually sit on the firing lines towers
  // shoot across the path — that is what gives line-of-sight cover real meaning
  // (direct-fire towers can't shoot through a blocker; the virus lob arcs over
  // it). Early attempts require the band; once half the attempt budget is spent
  // we relax to any in-bounds cell so a sparse layout can never fail to place.
  const LANE_MID_ROW = Math.floor(GRID_HEIGHT / 2);
  const LANE_BAND = 2;
  const MAX_BLOCKER_ATTEMPTS = 240;

  /**
   * The blocker placement, extracted verbatim so a restart can re-roll it
   * (2026-08-11). The DRAW ORDER is unchanged — `int(4)` for the count, then
   * `int(GRID_WIDTH)`/`int(GRID_HEIGHT)` per attempt — because the first call
   * below still feeds on the SHARED main stream and that pre-advance is
   * load-bearing for the full-run golden master.
   */
  function placeBlockers(src: Rng): void {
    const blockerCount = 3 + src.int(4);
    let placed = 0;
    let attempts = 0;
    while (placed < blockerCount && attempts < MAX_BLOCKER_ATTEMPTS) {
      attempts++;
      const bx = src.int(GRID_WIDTH);
      const by = src.int(GRID_HEIGHT);
      const cell = grid.getCell(bx, by);
      if (!cell || cell.type !== CellType.EMPTY) continue;
      if (bx <= 1 || bx >= GRID_WIDTH - 2) continue;
      // Lane bias: require central-band placement until the budget is half-spent.
      const inLaneBand = Math.abs(by - LANE_MID_ROW) <= LANE_BAND;
      if (!inLaneBand && attempts < MAX_BLOCKER_ATTEMPTS / 2) continue;
      grid.setCell(bx, by, CellType.BLOCKER);
      if (
        pathfinding.canPlace(
          {
            width: grid.width,
            height: grid.height,
            isBlocked: (gx, gy) => grid.getCell(gx, gy)?.blocksPath ?? false,
          },
          grid.getEntries(),
          grid.getExits(),
          bx,
          by,
        )
      ) {
        placed++;
      } else {
        grid.setCell(bx, by, CellType.EMPTY);
      }
    }
  }

  placeBlockers(rng);

  function resetSpawnLayout(seed: number): SpawnLayout {
    spawnLayout = chooseSpawnLayout(grid, pathfinding, seed, policy);
    applySpawnLayout(grid, spawnLayout);
    return cloneSpawnLayout(spawnLayout);
  }

  /**
   * Re-roll the procedural blockers for a new run — the missing half of "a
   * restart gives you a new board" (2026-08-11, owner).
   *
   * `resetSpawnLayout` already ran per run from `startRun`, so SPAWNS varied
   * across restarts; the blockers did not. They were placed once here at boot
   * and then stood forever, which is what made a restarted run read as the same
   * map with the same cover.
   *
   * Clears the old blockers first so placement sees an empty board, then draws
   * from an ISOLATED stream ({@link BLOCKER_SEED_SALT}) rather than the shared
   * one. Call it BEFORE `resetSpawnLayout` for the same seed: spawn eligibility
   * is path-validated against the blockers, so the order decides whether the
   * spawns are judged against the new cover or the old.
   */
  function regenerateBlockers(seed: number): void {
    for (let gx = 0; gx < GRID_WIDTH; gx++) {
      for (let gy = 0; gy < GRID_HEIGHT; gy++) {
        if (grid.getCell(gx, gy)?.type === CellType.BLOCKER) {
          grid.setCell(gx, gy, CellType.EMPTY);
        }
      }
    }
    placeBlockers(createRng((seed ^ BLOCKER_SEED_SALT) | 0));
  }

  resetSpawnLayout(rng.seed);

  let primaryPathCellCount = 0;

  // BUG-2 fix: Store the original primary entry/exit so recalculatePath()
  // always uses them, even after breach activates additional PATH_ENTRY cells.
  const primaryEntry: Vec2 = { x: 0, y: Math.floor(GRID_HEIGHT / 2) };
  const primaryExit: Vec2 = {
    x: GRID_WIDTH - 1,
    y: Math.floor(GRID_HEIGHT / 2),
  };

  const primaryPathGrid = {
    width: grid.width,
    height: grid.height,
    isBlocked: (gx: number, gy: number) => {
      const c = grid.getCell(gx, gy);
      return c?.blocksPath ?? false;
    },
  };

  function recalculatePath(): void {
    pathfinding.invalidateCache();
    pathRegistry.recalculateAll();
    gs.pathPixels = pathRegistry.getPixelPath("primary") ?? [];
    const gridPath = pathfinding.findPath(
      primaryPathGrid,
      primaryEntry,
      primaryExit,
    );
    primaryPathCellCount = gridPath?.length ?? 0;
  }

  // Register primary path with the registry
  pathRegistry.register("primary", primaryEntry, primaryExit, primaryPathGrid);
  gs.pathPixels = pathRegistry.getPixelPath("primary") ?? [];
  const initialGridPath = pathfinding.findPath(
    primaryPathGrid,
    primaryEntry,
    primaryExit,
  );
  primaryPathCellCount = initialGridPath?.length ?? 0;

  return {
    grid,
    pathfinding,
    pathRegistry,
    recalculatePath,
    resetSpawnLayout,
    regenerateBlockers,
    getSpawnLayout: () => cloneSpawnLayout(spawnLayout),
    get primaryPathCellCount() {
      return primaryPathCellCount;
    },
  };
}
